import React, { useState } from 'react';
import cx from 'classnames';

// eslint-disable-next-line restrict-imports/restrict-imports
import { makeStyles } from 'design-system/product/theme';

import { Text } from 'react-scale/components/design-system/Typography';
import { Button } from 'design-system/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';

export interface DraggingOptions {
  // use case: customer would drag around some switches and press a button once ordering is finalized
  draggableIndexStart: number; // sometimes we only want to allow a suffix of the responses to be draggable
  dropAction: (orderFrom: number, orderTo: number) => void;
  resetAction: () => void;
  staticHeader: string;
  draggableHeader: string;
  draggableDescription?: string;
  buttonTextPreToggle: string;
  buttonTextPostToggle: string;
  buttonAction: () => void;
  buttonDisabled?: boolean;
}

function RapidDraggableSwitch<T extends string>({
  id,
  values,
  currentValue,
  onSwitch,
  draggingOptions,
}: {
  id?: string;
  values: { id?: string; display: string; value: T; order: number }[];
  currentValue?: T;
  onSwitch: (value: T) => void;
  draggingOptions: DraggingOptions;
}): React.ReactElement {
  const valueToItem = (findValue: T | undefined) => _.find(values, v => v.value === findValue);
  const [heldValue, setHeldValue] = useState<T | undefined>(undefined);
  const [draggingEnabled, setDraggingEnabled] = useState(false);
  const classes = useStyles();

  const draggerSelect = (value: T) => {
    setHeldValue(value);
  };

  const draggerDrop = (e: any) => {
    if (!heldValue) {
      return;
    }
    // we want to now slide the current index over to the dropped index
    const dropValue = e.currentTarget.value;
    if (heldValue !== dropValue) {
      const heldItem = valueToItem(heldValue);
      const dropItem = valueToItem(dropValue);
      if (heldItem && dropItem) {
        draggingOptions.dropAction(heldItem.order, dropItem.order);
      }
    }
    setHeldValue(undefined);
  };

  function handleClickSwitch(value: T) {
    onSwitch(value);
  }

  const renderDraggableButtons = (isDraggable: boolean) => {
    return (
      <div className={cx(classes.buttons)}>
        <div className={cx(classes.sortingIndicator)}>Best</div>
        {values
          .slice(draggingOptions.draggableIndexStart)
          .sort((a, b) => a.order - b.order)
          .map(({ id, value, display }) => {
            const isSelected = currentValue === value;
            return (
              <button
                id={id}
                value={value}
                key={`${value}_${display}`}
                className={cx(classes.switchButton, {
                  [classes.selected]: isSelected,
                })}
                draggable={isDraggable}
                onClick={() => {
                  handleClickSwitch(value);
                }}
                onDragStart={() => draggerSelect(value)}
                onDragOver={e => e.preventDefault()}
                onDrop={draggerDrop}
              >
                {isDraggable ? (
                  <FontAwesomeIcon className={cx(classes.dragIcon)} icon="grip-vertical" />
                ) : undefined}
                <Text medium>{display}</Text>
              </button>
            );
          })}
        <div className={cx(classes.sortingIndicator)}>Worst</div>
      </div>
    );
  };

  return (
    <div id={id} className={cx(classes.outerWrapper)}>
      <div className={cx(classes.staticSection)}>
        {<div className={cx(classes.sectionTitle)}>{draggingOptions.staticHeader}</div>}
        <div className={cx(classes.buttons)}>
          {values.slice(0, draggingOptions.draggableIndexStart).map(({ id, value, display }) => {
            const isSelected = currentValue === value;
            return (
              <button
                id={id}
                key={`${value}_${display}`}
                className={cx(classes.switchButton, {
                  [classes.selected]: isSelected,
                  [classes.noPointerEvents]: isSelected,
                })}
                onClick={() => {
                  handleClickSwitch(value);
                }}
              >
                <Text medium>{display}</Text>
              </button>
            );
          })}
        </div>
      </div>
      <div className={cx(classes.draggableSection)}>
        <div className={cx(classes.sectionTitle)}>{draggingOptions.draggableHeader}</div>
        {!draggingEnabled ? (
          <div style={{ display: 'flex' }}>
            {renderDraggableButtons(false)}
            <Button
              className={cx(classes.generalButton, classes.primaryButton)}
              onClick={() => setDraggingEnabled(true)}
            >
              {draggingOptions.buttonTextPreToggle}
            </Button>
          </div>
        ) : (
          <div className={cx(classes.draggableBody)}>
            {draggingOptions.draggableDescription && (
              <div>{draggingOptions.draggableDescription}</div>
            )}
            <div
              style={{
                margin: '16px 0',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              {renderDraggableButtons(true)}
            </div>
            <div style={{ float: 'right' }}>
              <Button
                className={cx(classes.generalButton)}
                onClick={() => setDraggingEnabled(false)}
              >
                Collapse
              </Button>
              <Button className={cx(classes.generalButton)} onClick={draggingOptions.resetAction}>
                Reset
              </Button>
              <Button
                className={cx(classes.generalButton, classes.primaryButton)}
                onClick={draggingOptions.buttonAction}
                disabled={!!draggingOptions.buttonDisabled}
              >
                {draggingOptions.buttonTextPostToggle}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RapidDraggableSwitch;

const useStyles = makeStyles(theme => ({
  outerWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  staticSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  draggableSection: {
    flexGrow: 1,
    paddingLeft: '30px',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 'bold',
    color: theme.colors.gray50,
    marginBottom: '10px',
  },
  buttons: {
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.colors.white,
    padding: 2,
    borderRadius: theme.spacing(1),
    boxShadow: theme.containers.elevation1,
  },
  draggableBody: {
    background: theme.colors.white,
    padding: 20,
    borderRadius: theme.spacing(1),
    boxShadow: theme.containers.elevation1,
    marginRight: theme.containers.marginRightForElevation,
  },
  sortingIndicator: {
    display: 'inline-table',
    padding: `0 ${theme.spacing(2)}px`,
  },
  switchButton: {
    display: 'inline-table',
    color: theme.colors.gray50,
    background: 'transparent',
    minHeight: theme.spacing(3.5),
    padding: `0 ${theme.spacing(2)}px`,
    borderRadius: theme.spacing(1),
    border: '1px transparent solid',
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:not(:last-child)': {
      marginRight: 2,
    },
  },
  selected: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  noPointerEvents: {
    pointerEvents: 'none',
  },
  dragIcon: {
    color: theme.colors.gray50,
    padding: '0 4px',
  },
  generalButton: {
    marginLeft: '12px',
  },
  primaryButton: {
    backgroundColor: theme.colors.periwinkle100,
    color: theme.colors.white,
  },
}));
